import React, { useMemo, forwardRef } from "react";
import { Table, Button, Space } from "antd";
import { useQuery } from "react-query";
import { useIntl } from "react-intl";
import { getMetaData, getEntityForm } from "../../api/server";
import { fillCompanyForm } from "../util/docsPrint";
import { PrinterOutlined } from "@ant-design/icons";
import Surface from "../layouts/Surface";
/**
 * Table component for displaying and printing entity-specific forms.
 * Similar to PrintFormsModal but displays forms in a table format.
 *
 * @component
 * @param {Object} props
 * @param {string} props.entity - The type of entity (e.g., 'patient', 'employee', 'company')
 * @param {string} props.entityId - The unique identifier of the entity
 * @param {string} props.entityCollection - The collection name where the entity is stored
 */

const PrintFormsTable = forwardRef((props, ref) => {
  const { entity, entityId, entityCollection } = props;

  const intl = useIntl();

  // Fetch all forms
  const { data: response, isLoading } = useQuery("forms", () =>
    getMetaData({ name: "forms" })
  );

  const forms = response?.data?.items || [];

  const handlePrint = async (formId) => {
    try {
      const formData = await getEntityForm({
        entityCollection,
        entityId,
        formId,
      });
      await fillCompanyForm(formData.form, formData.Data);
    } catch (error) {
      console.error("Error printing form:", error);
    }
  };

  const columns = [
    {
      title: intl.formatMessage({ id: "app.settings.forms.name" }),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: intl.formatMessage({ id: "app.settings.forms.description" }),
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: intl.formatMessage({ id: "app.forms.actions" }),
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={() => handlePrint(record.id)}
            size="small"
          >
            {intl.formatMessage({ id: "app.actions.print" })}
          </Button>
        </Space>
      ),
    },
  ];

  // Filter forms by entity
  const filteredData = useMemo(() => {
    return forms.filter((form) => form.entities?.includes(entity));
  }, [forms, entity]);

  return (
    <div style={{}} ref={ref}>
      <Surface>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
          }}
        />
      </Surface>
    </div>
  );
});

export default PrintFormsTable;
